import type { DurationUnit } from '@_/types';
import type { Duration } from 'luxon';
import { DateTime } from 'luxon';

export type DayPeriod = 'am' | 'pm';

export interface HourMin24 {
  hour: number;
  minute: number;
}

export interface HourMin12 {
  dayPeriod: DayPeriod;
  hour: number;
  minute: number;
}

export const HOUR_MIN_24_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

export function isValidHourMin24(value: string): boolean {
  return HOUR_MIN_24_REGEX.test(value);
}

export function parseHourMin24(value: string): HourMin24 {
  const [hour, minute] = value.split(':');
  return {
    hour: parseInt(hour, 10),
    minute: parseInt(minute, 10)
  };
}

export function formatHourMin24({ hour, minute }: HourMin24): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function hourMin24ToSeconds(value: string): number {
  const { hour, minute } = parseHourMin24(value);
  return hour * 3600 + minute * 60;
}

export function hourMin24To12({ hour, minute }: HourMin24): HourMin12 {
  return {
    dayPeriod: hour >= 12 ? 'pm' : 'am',
    hour: hour % 12 || 12,
    minute
  };
}

export function hourMin12To24({ dayPeriod, hour, minute }: HourMin12): HourMin24 {
  return {
    hour: (hour % 12) + (dayPeriod === 'pm' ? 12 : 0),
    minute
  };
}

export function isValidDateTime(value: unknown): value is DateTime<true> {
  return DateTime.isDateTime(value) && value.isValid;
}

export function getDaysInMonth(year: number | string, month: number | string): number {
  const yearInt = typeof year === 'string' ? parseInt(year, 10) : year;
  const monthInt = typeof month === 'string' ? parseInt(month, 10) : month;
  return DateTime.local(yearInt, monthInt).daysInMonth!;
}

export function getDurationSignificantUnit(duration: Duration): {
  unit: DurationUnit;
  value: number;
} {
  const units = ['years', 'months', 'days', 'hours', 'minutes', 'seconds'] as const;
  const durationObj = duration.shiftTo(...units).toObject();
  const largestUnit = units.find(unit => durationObj[unit]);
  return largestUnit
    ? {
        unit: largestUnit,
        value: Math.floor(durationObj[largestUnit] || 0)
      }
    : {
        unit: 'seconds',
        value: 0
      };
}

export function assertISODateString(value: unknown): asserts value is string {
  if (typeof value !== 'string' || !DateTime.fromISO(value).isValid) {
    throw new Error('Invalid date string');
  }
}
